import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Trophy, Users } from 'lucide-react';
import type { CaregiverRank } from '@/hooks/useCompanyAnalytics';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const MEDAL_COLORS = ['text-yellow-500', 'text-gray-400', 'text-amber-600'];

interface Props {
  ranking: CaregiverRank[];
}

function PctCell({ value }: { value: number | null }) {
  if (value === null) return <span className="text-muted-foreground">--</span>;
  const color = value >= 80 ? 'text-success' : value >= 50 ? 'text-warning' : 'text-destructive';
  return <span className={color}>{value}%</span>;
}

export function CaregiverRankingTable({ ranking }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (ranking.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          {t('analytics.teamPerformance')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t('analytics.caregiver')}</TableHead>
                <TableHead className="text-center">{t('analytics.shifts')}</TableHead>
                <TableHead className="text-center">{t('analytics.checkinPunctuality')}</TableHead>
                <TableHead className="text-center">{t('analytics.tasks')}</TableHead>
                <TableHead className="text-center">{t('analytics.punctuality')}</TableHead>
                <TableHead className="text-center">{t('analytics.incidentsCol')}</TableHead>
                <TableHead className="text-center">{t('analytics.lastShift')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ranking.map((c, idx) => (
                <TableRow key={c.user_id}>
                  <TableCell className="font-bold">
                    {idx < 3 ? (
                      <Trophy className={`h-4 w-4 ${MEDAL_COLORS[idx]}`} />
                    ) : (
                      idx + 1
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <SignedAvatar
                        src={c.avatar_url}
                        bucket="avatars"
                        className="h-7 w-7"
                        fallback={
                          <span className="bg-primary/10 text-primary text-xs flex items-center justify-center w-full h-full">
                            {c.name.charAt(0).toUpperCase()}
                          </span>
                        }
                      />
                      <button
                        onClick={() => navigate(`/app/company/caregiver/${c.user_id}`)}
                        className="text-sm truncate max-w-[120px] text-left hover:underline text-primary"
                      >
                        {c.name}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {c.shifts_count > 0 ? c.shifts_count : <span className="text-muted-foreground">--</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <PctCell value={c.checkin_on_time_pct} />
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {c.total_tasks > 0 ? c.total_tasks : <span className="text-muted-foreground">--</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <PctCell value={c.on_time_pct} />
                  </TableCell>
                  <TableCell className="text-center">{c.incidents}</TableCell>
                  <TableCell className="text-center text-sm">
                    {c.last_shift_date ? (
                      format(new Date(c.last_shift_date), 'dd/MM', { locale: i18n.language === 'pt-BR' ? ptBR : undefined })
                    ) : (
                      <span className="text-muted-foreground">--</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
